/**
 * Everything that is not length.
 *
 * These three settings are picked once and then left alone for a session, which
 * is exactly the profile for progressive disclosure. Each gets its real name
 * and a sentence saying what it does — "Panel" on a chip told nobody that three
 * referees were checking the physics.
 */

import { Settings2 } from "lucide-react";
import { MODELS, type ModelId, type ReasoningEffort } from "../../../shared/models.ts";
import type { SendSettings } from "../../state/useChat.ts";
import { Button } from "../ui/button.tsx";
import { Label } from "../ui/label.tsx";
import { Switch } from "../ui/switch.tsx";
import { Separator } from "../ui/separator.tsx";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover.tsx";

interface SettingsPopoverProps {
  readonly settings: SendSettings;
  readonly onChange: (settings: SendSettings) => void;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

const EFFORTS: readonly { value: ReasoningEffort; label: string }[] = [
  { value: "low", label: "light" },
  { value: "medium", label: "medium" },
  { value: "high", label: "deep" },
];

export function SettingsPopover({
  settings,
  onChange,
  open,
  onOpenChange,
}: SettingsPopoverProps) {
  const model = MODELS.find((entry) => entry.id === settings.model) ?? MODELS[0];

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Answer settings"
          className="text-muted-foreground hover:text-foreground"
        >
          <Settings2 />
        </Button>
      </PopoverTrigger>

      {/* 20rem, unless the viewport is narrower than that plus its margins. */}
      <PopoverContent
        align="start"
        className="w-[min(20rem,calc(100vw-2rem))] font-sans"
      >
        <Field
          label="Model"
          hint={model?.blurb ?? ""}
          control={
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={settings.model}
              onValueChange={(next) => {
                if (next) onChange({ ...settings, model: next as ModelId });
              }}
              aria-label="Model"
            >
              {MODELS.map((entry) => (
                <ToggleGroupItem
                  key={entry.id}
                  value={entry.id}
                  className="text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  {entry.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          }
        />

        <Separator className="my-3" />

        <Field
          label="Review panel"
          labelFor="setting-supervised"
          hint="Three referees check the physics, the maths, and the figures, then an editor writes up anything they found."
          control={
            <Switch
              id="setting-supervised"
              checked={settings.supervised}
              onCheckedChange={(supervised) =>
                onChange({ ...settings, supervised })
              }
            />
          }
        />

        <Separator className="my-3" />

        <Field
          label="Extended thinking"
          labelFor="setting-thinking"
          hint="Reason at length before answering. Slower, and worth it for a hard derivation."
          control={
            <Switch
              id="setting-thinking"
              checked={settings.thinking}
              onCheckedChange={(thinking) => onChange({ ...settings, thinking })}
            />
          }
        />

        {/* Revealed here rather than in the composer row, where it used to
            appear and disappear and reflow everything beside it. */}
        {settings.thinking && (
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={settings.reasoningEffort}
            onValueChange={(next) => {
              if (next) {
                onChange({ ...settings, reasoningEffort: next as ReasoningEffort });
              }
            }}
            aria-label="Reasoning effort"
            className="mt-2"
          >
            {EFFORTS.map((effort) => (
              <ToggleGroupItem
                key={effort.value}
                value={effort.value}
                className="text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                {effort.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface FieldProps {
  readonly label: string;
  readonly labelFor?: string;
  readonly hint: string;
  readonly control: React.ReactNode;
}

function Field({ label, labelFor, hint, control }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={labelFor} className="text-sm">
          {label}
        </Label>
        {control}
      </div>
      <p className="text-xs leading-snug text-muted-foreground">{hint}</p>
    </div>
  );
}
